// API client tipado pra `/api/crm/deals` (Story 11.4 — CRUD + lifecycle).
// Padrão: wrapper sobre `apiClient` (axios) + Zod parse nas respostas (Tier 2).
//
// Fonte de tipos: schemas Zod do `@control-center/shared` (back+front).
// Convenção de envelope do backend:
//  - lista: { items, meta: { next_cursor, total } }
//  - kanban (?view=kanban): { data: { [stage]: CrmDeal[] } }
//  - single: { data }
//
// `value` (Decimal) vem como string do backend (convenção de dinheiro).

import { z } from 'zod';

import {
  crmDealStageSchema,
  crmDealStatusSchema,
  saleOriginSchema,
  type ChangeCrmDealStageDto,
  type CreateCrmDealDto,
  type UpdateCrmDealDto,
} from '@control-center/shared';

import { apiClient } from './api-client';

// -----------------------------------------------------------------------------
// Schemas de resposta (Tier 2 — valida o shape que o backend devolve)
// -----------------------------------------------------------------------------

const relationSchema = z.object({ id: z.string(), name: z.string() }).nullable();

const crmDealSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  lead_id: z.string().nullable(),
  client_id: z.string().nullable(),
  saas_product_id: z.string().nullable(),
  plan_id: z.string().nullable(),
  value: z.string().nullable(),
  stage: crmDealStageSchema,
  status: crmDealStatusSchema,
  expected_close_date: z.string().nullable(),
  responsible_user_id: z.string().nullable(),
  origin: saleOriginSchema.nullable(),
  lost_reason: z.string().nullable(),
  stage_entered_at: z.string(),
  version: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CrmDealEntity = z.infer<typeof crmDealSchema>;

const dealProposalSummarySchema = z.object({
  id: z.string(),
  status: z.string(),
  proposal_number: z.string(),
});

const crmDealDetailSchema = crmDealSchema.extend({
  lead: relationSchema.and(z.object({ temperature: z.string() }).partial()).nullable(),
  client: relationSchema,
  saas_product: relationSchema,
  plan: relationSchema,
  responsible_user: relationSchema,
  proposals: z.array(dealProposalSummarySchema),
});
export type CrmDealDetailEntity = z.infer<typeof crmDealDetailSchema>;

const listResponseSchema = z.object({
  items: z.array(crmDealSchema),
  meta: z.object({ next_cursor: z.string().nullable(), total: z.number() }),
});
export type CrmDealListResponse = z.infer<typeof listResponseSchema>;

// Kanban (?view=kanban): { data: { [stage]: CrmDeal[] } } — 7 estágios sempre
// presentes (colunas vazias = []). O backend agrupa deals `open` por estágio.
const kanbanResponseSchema = z.object({
  data: z.record(crmDealStageSchema, z.array(crmDealSchema)),
});
/** Mapa estágio → deals. Colunas vazias vêm como []. */
export type CrmDealKanban = Record<CrmDealEntity['stage'], CrmDealEntity[]>;

const singleResponseSchema = z.object({ data: crmDealSchema });
const detailResponseSchema = z.object({ data: crmDealDetailSchema });

export interface CrmDealListParams {
  cursor?: string;
  limit?: number;
  status?: CrmDealEntity['status'];
  stage?: CrmDealEntity['stage'];
  responsible_user_id?: string;
  saas_product_id?: string;
  origin?: NonNullable<CrmDealEntity['origin']>;
  value_min?: number;
  value_max?: number;
  stalled_days?: number;
}

/** GET /api/crm/deals — lista paginada por cursor + filtros. */
export async function fetchCrmDeals(
  params: CrmDealListParams = {},
  signal?: AbortSignal,
): Promise<CrmDealListResponse> {
  const { data } = await apiClient.get('/api/crm/deals', { params, signal });
  return listResponseSchema.parse(data);
}

export interface CrmDealKanbanParams {
  responsible_user_id?: string;
  saas_product_id?: string;
  origin?: NonNullable<CrmDealEntity['origin']>;
  value_min?: number;
  value_max?: number;
  stalled_days?: number;
}

/**
 * GET /api/crm/deals?view=kanban — deals `open` agrupados por estágio.
 * Sempre devolve as 7 chaves de estágio (colunas vazias = []) — garantimos isso
 * normalizando o `data` contra um mapa vazio (defesa caso o backend omita alguma).
 */
export async function fetchCrmDealsKanban(
  params: CrmDealKanbanParams = {},
  signal?: AbortSignal,
): Promise<CrmDealKanban> {
  const { data } = await apiClient.get('/api/crm/deals', {
    params: { ...params, view: 'kanban' },
    signal,
  });
  const parsed = kanbanResponseSchema.parse(data).data;
  const empty = emptyKanban();
  for (const stage of Object.keys(parsed) as CrmDealEntity['stage'][]) {
    empty[stage] = parsed[stage] ?? [];
  }
  return empty;
}

/** Mapa kanban vazio com todos os estágios — fonte única da ordem de colunas. */
function emptyKanban(): CrmDealKanban {
  return {
    novo_lead: [],
    qualificacao: [],
    diagnostico: [],
    proposta_enviada: [],
    negociacao: [],
    fechamento_ganho: [],
    fechamento_perdido: [],
  };
}

/** GET /api/crm/deals/:id — detalhe com relações + propostas. */
export async function fetchCrmDeal(id: string, signal?: AbortSignal): Promise<CrmDealDetailEntity> {
  const { data } = await apiClient.get(`/api/crm/deals/${id}`, { signal });
  return detailResponseSchema.parse(data).data;
}

/** POST /api/crm/deals — cria deal (default stage=novo_lead, status=open). */
export async function createCrmDeal(payload: CreateCrmDealDto): Promise<CrmDealEntity> {
  const { data } = await apiClient.post('/api/crm/deals', payload);
  return singleResponseSchema.parse(data).data;
}

/** PUT /api/crm/deals/:id — atualiza (optimistic lock via version). */
export async function updateCrmDeal(id: string, payload: UpdateCrmDealDto): Promise<CrmDealEntity> {
  const { data } = await apiClient.put(`/api/crm/deals/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}

/** PUT /api/crm/deals/:id/stage — muda estágio / fecha won/lost. */
export async function changeCrmDealStage(
  id: string,
  payload: ChangeCrmDealStageDto,
): Promise<CrmDealEntity> {
  const { data } = await apiClient.put(`/api/crm/deals/${id}/stage`, payload);
  return singleResponseSchema.parse(data).data;
}
